import { cloneDeep } from 'lodash';
import * as React from 'react';
import Slider, { Settings } from 'react-slick';
import ReactYoutube from 'react-youtube';

import Close from '../../../../../assets/images/close.svg';
import { Portal } from '../../../../../components/Portal';
import { getYoutubeVideoId } from '../../../../../utils';
import { EProjectType } from '../../../enums';
import { IDetailedComponentSource, SingleProjectComponentsContent } from '../../../models';
import { Arrow } from '../arrow';

import './index.scss';

interface IProps {
    content: SingleProjectComponentsContent[] | null;
    isOpen: boolean;
    onClose: () => void;
    selectedSrc: string | null;
}

const carouselConfig: Settings = {
    nextArrow: <Arrow to={'next'} />,
    prevArrow: <Arrow to={'prev'} />,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
};

export const DetailedProject: React.FC<IProps> = ({
    content,
    isOpen,
    onClose,
    selectedSrc
}) => {
    const filterSources = (sources: IDetailedComponentSource[]): IDetailedComponentSource[] => {
        // find the index of selected source
        const index = sources.findIndex(source => source.src === selectedSrc);
        const _filteredSources = cloneDeep(sources);

        // move first element to the end of the array until iteration reaces index.
        for (let i = 0; i < index; i++) {
            const tmp: IDetailedComponentSource = _filteredSources[0];
            _filteredSources.shift();
            _filteredSources.push(tmp);
        }

        return _filteredSources;
    };

    const renderContent = (componentContent: SingleProjectComponentsContent[] | null): JSX.Element | null => {
        let _content: JSX.Element | null = null;
        const sources: IDetailedComponentSource[] = [];

        componentContent?.forEach(component => {
            if (component.type === EProjectType.embedded_video) {
                sources.push({
                    src: component.content.url,
                    type: 'video'
                });
            } else if (component.type === EProjectType.multiple_media) {
                component.content.forEach(img => {
                    sources.push({
                        src: img,
                        type: 'img'
                    });
                });
            } else if (component.type === EProjectType.single_media) {
                sources.push({
                    src: component.content,
                    type: 'img'
                });
            } else if (component.type === EProjectType.slider) {
                component.content.forEach(img => {
                    sources.push({
                        src: img,
                        type: 'img'
                    });
                });
            } else if (component.type === EProjectType.triple_media) {
                component.content.forEach(img => {
                    sources.push({
                        src: img,
                        type: 'img',
                        portrait: true
                    });
                });
            }
        });

        const filteredSources = filterSources(sources);

        _content = (
            <Slider {...carouselConfig} className="detailed__content__slider">
                {
                    filteredSources.map(({ src, type, portrait }, i) => {
                        if (type === 'img') {
                            return (
                                <div
                                    key={i}
                                    className={`detailed__content__slider-item ${portrait && 'portrait'}`}
                                >
                                    {portrait ? (
                                        <div className="detailed__content__slider-item portrait__item">
                                            <img src={src} alt="" />
                                        </div>
                                    ) : (
                                        <img src={src} alt="" />
                                    )}
                                </div>
                            );
                        } else {
                            const query = getYoutubeVideoId(src);

                            return (
                                <div key={i} className="detailed__content__slider-item">
                                    <ReactYoutube
                                        videoId={query[1]}
                                        className="main-page_carousel-video"
                                    />
                                </div>
                            );
                        }
                    })
                }
            </Slider>
        );

        return _content;
    };

    const renderPortal = (): JSX.Element => {
        return (
            <Portal>
                <div className="detailed">
                    <div
                        className="detailed__close"
                        onClick={onClose}
                    >
                        <img src={Close} alt="" />
                    </div>
                    <div className="detailed__content">
                        {renderContent(content)}
                    </div>
                </div>
            </Portal>
        );
    };

    return (
        isOpen ? renderPortal() : null
    );
};
